import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  InputAdornment,
  AppBar,
  Toolbar,
  Avatar
} from '@mui/material';
import {
  Group as GroupIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  PowerSettingsNew as PowerIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  AccountCircle,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import teamService, { Team } from '../services/teamService';
import { useAuth } from '../contexts/AuthContext';
import TeamAccessRequestsPanel from '../components/TeamAccessRequestsPanel';

const TeamsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<'activate' | 'deactivate' | null>(null);

  const loadTeams = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await teamService.getMyTeams();
      setTeams(data);
    } catch (err: any) {
      setError('Failed to load teams. Please try again later.');
      console.error('Error loading teams:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTeams();
  }, [loadTeams]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleProfileClick = () => {
    navigate('/profile');
  };

  const handleTeamAction = async () => {
    if (!selectedTeam || !actionType) return;

    try {
      if (actionType === 'activate') {
        await teamService.activateTeam(selectedTeam.id);
      } else {
        await teamService.deactivateTeam(selectedTeam.id);
      }
      
      setActionDialogOpen(false);
      setSelectedTeam(null);
      setActionType(null);
      loadTeams(); // Reload teams to reflect changes
    } catch (err) {
      console.error(`Error ${actionType}ing team:`, err);
      setError(`Failed to ${actionType} team. Please try again.`);
    }
  };

  const openActionDialog = (team: Team, action: 'activate' | 'deactivate') => {
    setSelectedTeam(team);
    setActionType(action);
    setActionDialogOpen(true);
  };

  const filteredTeams = teams.filter(team =>
    team.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (team.departmentName && team.departmentName.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getStatusColor = (status: string) => {
    switch (status.toUpperCase()) {
      case 'ACTIVE':
        return 'success';
      case 'INACTIVE':
        return 'default';
      default:
        return 'default';
    }
  };

  const getMembershipStatusColor = (status?: string) => {
    if (!status) return undefined;
    switch (status.toLowerCase()) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'denied':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <AppBar position="static">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate('/dashboard')}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Student Psychology Profile System
          </Typography>
          <Typography variant="body1" sx={{ mr: 2 }}>
            {user?.firstName} {user?.lastName}
          </Typography>
          <IconButton color="inherit" onClick={handleProfileClick}>
            <AccountCircle />
          </IconButton>
          <Button color="inherit" onClick={handleLogout}>
            Logout
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, flexGrow: 1 }}>
        <Box mb={3}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h4" component="h1">
              Teams/Groups Management
            </Typography>
            <Box>
              <IconButton onClick={loadTeams} color="primary" title="Refresh">
                <RefreshIcon />
              </IconButton>
              {user?.userType === 'admin' && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/teams/create')}
                  sx={{ ml: 1 }}
                >
                  Create Team
                </Button>
              )}
            </Box>
          </Box>

          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search teams by name or department..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3 }}
          />
        </Box>

        {/* Team Access Requests Panel for Providers */}
        {user?.userType === 'provider' && (
          <TeamAccessRequestsPanel 
            onRequestAccess={(teamId?: number) => {
              // Navigate to team details or handle team access request
              if (teamId) {
                navigate(`/teams/${teamId}/request-access`);
              }
            }}
          />
        )}

        <Box mb={3}>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {filteredTeams.length === 0 ? (
            <Box textAlign="center" py={5}>
              <GroupIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                {searchTerm ? 'No teams found matching your search' : 'No teams available'}
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {filteredTeams.map((team) => (
                <Grid item xs={12} sm={6} md={4} key={team.id}>
                  <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                        <Typography variant="h6" component="h2">
                          {team.name}
                        </Typography>
                        <Chip
                          label={team.status}
                          color={getStatusColor(team.status)}
                          size="small"
                        />
                      </Box>

                      {team.departmentName && (
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Department: {team.departmentName}
                        </Typography>
                      )}

                      {team.location && (
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Location: {team.location}
                        </Typography>
                      )}

                      {team.membershipStatus && (
                        <Box mt={2}>
                          <Chip
                            label={`Membership: ${team.membershipStatus}`}
                            color={getMembershipStatusColor(team.membershipStatus)}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      )}

                      <Box mt={2} display="flex" gap={1}>
                        <Button
                          size="small"
                          startIcon={<VisibilityIcon />}
                          onClick={() => navigate(`/teams/${team.id}`)}
                        >
                          View
                        </Button>
                        {team.status === 'ACTIVE' ? (
                          <Button
                            size="small"
                            color="error"
                            startIcon={<PowerIcon />}
                            onClick={() => openActionDialog(team, 'deactivate')}
                          >
                            Deactivate
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            color="success"
                            startIcon={<PowerIcon />}
                            onClick={() => openActionDialog(team, 'activate')}
                          >
                            Activate
                          </Button>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Container>

      {/* Action Confirmation Dialog */}
      <Dialog
        open={actionDialogOpen}
        onClose={() => setActionDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {actionType === 'activate' ? 'Activate Team' : 'Deactivate Team'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to {actionType} the team "{selectedTeam?.name}"?
            {actionType === 'deactivate' && (
              ' This will prevent students from joining this team.'
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleTeamAction}
            color={actionType === 'activate' ? 'success' : 'error'}
            variant="contained"
          >
            {actionType === 'activate' ? 'Activate' : 'Deactivate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TeamsPage;